import { parseUrlInput } from "../normalizeInput";
import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const LEGACY_URL_PATTERN = "https://[^/]*\\.cribl\\.cloud/.*";

function workspaceApiBase(baseUrl: string | undefined): string {
  const origin = parseUrlInput(baseUrl ?? "")?.origin;
  return origin ? `${origin}/api/v1` : "";
}

export const cribl = defineConnector({
  identifier: "cribl",
  name: "Cribl",
  tagLine: "Data pipeline and observability",
  logo,
  protocol: "http",
  tags: ["featured", "engineering"],
  defaultUrlPattern: ({ baseUrl }) => {
    const apiBase = workspaceApiBase(baseUrl);
    return apiBase ? domainToRegex(apiBase) : LEGACY_URL_PATTERN;
  },
  auth: oauthAuth,
  steps: [
    {
      title: "Create API credentials in Cribl",
      items: [
        {
          kind: "markdown",
          content: `1. Log in to your [Cribl.Cloud](https://app.cribl.cloud) account as an owner or admin
2. Select **Products → Cribl**
3. Select **Organization → API Credentials**
4. Select **Add Credential**
5. Enter a name and select the appropriate permissions
6. Select **Save**
7. Copy the **Client ID** and **Client Secret**`,
        },
      ],
    },
    {
      title: "Enter your workspace and credentials",
      items: [
        {
          kind: "baseUrl",
          label: "Workspace URL",
          required: true,
          placeholder: "https://workspaceName-organizationId.cribl.cloud",
          helperText:
            "From Workspace → Access in Cribl.Cloud. Either the workspace URL or the /api/v1 API base works.",
          target: { kind: "configuration", field: "baseUrl" },
        },
        {
          kind: "input",
          label: "Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "client_credentials",
    tokenEndpoint: () => "https://login.cribl.cloud/oauth/token",
    clientCredentialsFrom: "db",
    tokenEndpointAuthMethod: () => "client_secret_post",
    scopes: () => [],
    authorizationParameters: () => ({
      audience: "https://api.cribl.cloud",
    }),
  },
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) => {
      const apiBase = workspaceApiBase(baseUrl);
      return {
        url: apiBase ? `${apiBase}/system/info` : "",
        method: "GET",
      };
    },
  },
  publicEnvVars: ({ baseUrl }) => ({
    CRIBL_BASE_URL: workspaceApiBase(baseUrl),
  }),
});
